import React, { useState } from 'react';
import { TRADES } from '../../lib/subcontractors';
import { ModalShell, Field, Label, ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';

/* Add a subcontractor, or edit one's info (not its standing: that's a change with a reason, on its
   page). `sub` null = a new one; onSubmit(payload) saves, and throws to keep the dialog open. */
const TEXT = ['contact_name', 'contact_title', 'contact_phone', 'contact_email', 'address_line1', 'address_line2', 'city', 'state', 'zip'];

const SubcontractorForm = ({ sub, onClose, onSubmit }) => {
  const isEdit = !!sub;
  const [form, setForm] = useState(() => ({
    name: sub?.name || '',
    trades: sub?.trades || [],
    since_year: sub?.since_year ? String(sub.since_year) : '',
    tax_id: '',
    ...Object.fromEntries(TEXT.map((k) => [k, sub?.[k] || ''])),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTrade = (t) => setForm((f) => ({ ...f, trades: f.trades.includes(t) ? f.trades.filter((x) => x !== t) : [...f.trades, t] }));

  const submit = async () => {
    if (busy) return;
    if (!form.name.trim()) return setError('A company name is required.');
    const year = form.since_year.trim();
    if (year && !/^(19|20)\d{2}$/.test(year)) return setError('Working with E3 since: a year like 2021.');
    const taxDigits = form.tax_id.replace(/\D/g, '');
    if (form.tax_id.trim() && taxDigits.length !== 9) return setError('A tax ID has 9 digits.');
    const payload = {
      name: form.name.trim(),
      trades: TRADES.filter((t) => form.trades.includes(t)),
      since_year: year ? Number(year) : null,
      ...Object.fromEntries(TEXT.map((k) => [k, form[k].trim()])),
    };
    if (taxDigits) payload.tax_id = taxDigits; // blank keeps what's on file
    setError(null);
    setBusy(true);
    try {
      await onSubmit(payload);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 };
  return (
    <ModalShell
      title={isEdit ? `Edit ${sub.name}` : 'New subcontractor'}
      subtitle={isEdit ? 'Their company info. Change their standing from the Standing button on their page.' : 'It starts as Approved. Add their MSA, insurance and other documents on its page.'}
      onClose={onClose} busy={busy} width={600}
      footer={(
        <>
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={submit}>{busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add subcontractor'}</PrimaryButton>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <Field id="sub-name" label="Company" required value={form.name} onChange={set('name')} placeholder="e.g. Ferguson Veresh, Inc" autoFocus={!isEdit} />
        <div>
          <Label hint="the same list as the subcontractor portal">Trades</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {TRADES.map((t) => {
              const on = form.trades.includes(t);
              return (
                <button key={t} type="button" aria-pressed={on} onClick={() => toggleTrade(t)} style={{
                  height: 30, padding: '0 11px', borderRadius: 999, fontSize: 13.5, fontWeight: on ? 600 : 500, cursor: 'pointer',
                  color: on ? 'var(--accent)' : 'var(--text-2)', fontFamily: 'var(--font-sans)',
                  background: on ? 'color-mix(in oklch, var(--accent) 12%, var(--bg-surface))' : 'var(--bg-surface)',
                  border: `1px solid ${on ? 'color-mix(in oklch, var(--accent) 45%, transparent)' : 'var(--border-default)'}`,
                }}>{t}</button>
              );
            })}
          </div>
        </div>
        <div style={grid2}>
          <Field id="sub-since" label="Working with E3 since" value={form.since_year} onChange={set('since_year')} placeholder="e.g. 2021" inputMode="numeric" maxLength={4} />
          <Field id="sub-tax" label="Tax ID" hint={isEdit && sub.has_tax_id ? `${sub.tax_id_masked} on file` : '9 digits'}
            value={form.tax_id} onChange={set('tax_id')} placeholder={isEdit && sub.has_tax_id ? 'Type a new one to replace it' : 'XX-XXXXXXX'} autoComplete="off" />
        </div>
        <div style={{ height: 1, background: 'var(--border-subtle)' }} />
        <div style={grid2}>
          <Field id="sub-contact" label="Main contact" value={form.contact_name} onChange={set('contact_name')} placeholder="Name" />
          <Field id="sub-title" label="Title" value={form.contact_title} onChange={set('contact_title')} placeholder="e.g. Project manager" />
          <Field id="sub-phone" label="Phone" value={form.contact_phone} onChange={set('contact_phone')} type="tel" />
          <Field id="sub-email" label="Email" value={form.contact_email} onChange={set('contact_email')} type="email" />
        </div>
        <div style={{ height: 1, background: 'var(--border-subtle)' }} />
        <Field id="sub-addr1" label="Address" value={form.address_line1} onChange={set('address_line1')} placeholder="Street" />
        <Field id="sub-addr2" value={form.address_line2} onChange={set('address_line2')} placeholder="Suite, floor (optional)" aria-label="Address line 2" />
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
          <Field id="sub-city" label="City" value={form.city} onChange={set('city')} />
          <Field id="sub-state" label="State" value={form.state} onChange={set('state')} placeholder="TX" />
          <Field id="sub-zip" label="ZIP" value={form.zip} onChange={set('zip')} inputMode="numeric" />
        </div>
      </div>
    </ModalShell>
  );
};

export default SubcontractorForm;
